/**
 * `<TranscriptDropzone>` — drag-drop zone for interview transcript files.
 *
 * B-13.3 cofounder brief §2 — default input for Interview Analysis tab.
 * Reuses visual + behavioural pattern from `<ResumeDropzone>` (same
 * border-dashed, drag-over highlight, click-to-browse, accepted-types
 * sub-line) but is a sibling component: different accepted extensions,
 * single-file only (no multi-upload), distinct server endpoint.
 *
 * Accepted file types:
 *   .txt  — plain text (Otter / Granola / copy-paste dump)
 *   .docx — Microsoft Word (Teams auto-transcript)
 *   .pdf  — PDF transcript export
 *   .vtt  — WebVTT (Riverside / Zoom / Google Meet)
 *   .srt  — SubRip (Zoom / Teams / legacy tools)
 *
 * Audio files (.mp3, .m4a, …) are explicitly REJECTED with a clear
 * error message — deferred to P1-calendar-integration per brief §2.
 *
 * On drop/select: fires `onFile(file)` with the chosen File. The parent
 * (InterviewAnalysisTab) submits it to the server and transitions to
 * the Generating state.
 *
 * Server is the security authority; frontend extension filtering is UX
 * only. 15 MB size cap (matching ResumeDropzone).
 */
import {
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type JSX,
} from "react";

const ACCEPTED_EXTS = [".txt", ".docx", ".pdf", ".vtt", ".srt"] as const;
const AUDIO_EXTS = [".mp3", ".m4a", ".wav", ".ogg", ".aac", ".flac", ".m4b"];
const ACCEPTED_INPUT_VALUE = ACCEPTED_EXTS.join(",");

const MAX_BYTES = 15 * 1024 * 1024; // 15 MB — matches ResumeDropzone

function getExt(filename: string): string {
  const idx = filename.lastIndexOf(".");
  if (idx === -1) return "";
  return filename.slice(idx).toLowerCase();
}

function isAccepted(file: File): boolean {
  return (ACCEPTED_EXTS as readonly string[]).includes(getExt(file.name));
}

function isAudio(file: File): boolean {
  return AUDIO_EXTS.includes(getExt(file.name));
}

interface TranscriptDropzoneProps {
  /** Called with the chosen file after extension + size check. */
  onFile: (file: File) => void;
  /** Called when a file is rejected with a user-readable reason. */
  onReject?: (reason: string) => void;
  /** Disable interaction (e.g. while a mutation is in flight). */
  disabled?: boolean;
}

export function TranscriptDropzone({
  onFile,
  onReject,
  disabled = false,
}: TranscriptDropzoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleFiles(files: File[]): void {
    if (disabled) return;
    const first = files[0];
    if (!first) return;

    if (isAudio(first)) {
      onReject?.(
        "Audio uploads are not yet supported. Upload a .txt, .pdf, .docx, .vtt, or .srt transcript instead.",
      );
      return;
    }
    if (!isAccepted(first)) {
      onReject?.(
        `Unsupported file type (${getExt(first.name) || "unknown"}). Accepted: .txt .pdf .docx .vtt .srt`,
      );
      return;
    }
    if (first.size > MAX_BYTES) {
      onReject?.("File exceeds 15 MB — please trim or paste the transcript instead.");
      return;
    }
    onFile(first);
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (disabled) return;
    handleFiles(Array.from(e.target.files ?? []));
    e.target.value = "";
  };

  const handleClick = (): void => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const ringClass = dragOver
    ? "border-accent-secondary ring-2 ring-accent-secondary/20"
    : "border-border-default";
  const bgClass = dragOver ? "bg-accent-secondary/5" : "bg-surface-subtle/40";

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      data-testid="transcript-dropzone"
      data-drag-over={dragOver ? "y" : "n"}
      data-disabled={disabled ? "y" : "n"}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      aria-disabled={disabled}
      className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-5 text-center transition-colors ${
        disabled
          ? "cursor-not-allowed opacity-60"
          : "cursor-pointer hover:border-[var(--border-focus)]"
      } ${ringClass} ${bgClass}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_INPUT_VALUE}
        className="hidden"
        onChange={handleChange}
        aria-label="Choose transcript file"
        disabled={disabled}
      />
      <UploadCloudIcon disabled={disabled} dragOver={dragOver} />
      <p
        className={`text-base font-semibold ${
          disabled ? "text-text-disabled" : "text-text-primary"
        }`}
      >
        {dragOver
          ? "Drop to upload"
          : "Drop interview transcript here or click to browse"}
      </p>
      <p
        className={`text-sm ${
          disabled ? "text-text-disabled" : "text-text-secondary"
        }`}
      >
        TXT, DOCX, PDF, VTT, or SRT
      </p>
      <p
        className={`text-xs ${
          disabled ? "text-text-disabled" : "text-text-tertiary"
        }`}
      >
        15 MB max
      </p>
    </div>
  );
}

function UploadCloudIcon({
  disabled,
  dragOver,
}: {
  disabled: boolean;
  dragOver: boolean;
}): JSX.Element {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`${
        disabled ? "text-text-disabled" : "text-text-tertiary"
      } ${dragOver ? "scale-110 transition-transform" : ""}`}
    >
      <path d="M14 32a8 8 0 010-16 12 12 0 0123 4 7 7 0 011 13" />
      <path d="M24 22v16" />
      <path d="M18 28l6-6 6 6" />
    </svg>
  );
}
